import type { Key } from 'react';
import {
  AlignStack,
  Badge,
  ComboBox,
  ComboBoxItem,
  ComboBoxSection,
  FeatureBadge,
  Icon,
  Label,
  SkeletonGroup,
  SmallSansSizes,
  styled,
  Text,
  ToggleInput,
  useCurrentCrumbs,
  useViewport,
} from '@meterup/atto';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router';

import { ALL_NETWORKS_SLUG, MULTISITE_PAGES } from '../../../../constants';
import { getActiveControllerForNetwork } from '../../../../hooks/useActiveControllerForNetwork';
import { useIsActiveMatcher } from '../../../../hooks/useIsActive';
import { useNetworkSlugFromPathOrNull } from '../../../../hooks/useNetworkFromPath';
import { type Network, useNetworksForCompany } from '../../../../hooks/useNetworksForCompany';
import { useCurrentCompany } from '../../../../providers/CurrentCompanyProvider';
import { getNextBestCurrentPathForNetwork } from '../../../../utils/bestPath';
import { makeLink } from '../../../../utils/main_and_drawer_navigation';
import { getNetworkAddress } from '../../../../utils/network';
import useAccessLevelForUser from '../../../HasAccess/useAccessLevelForUser';
import {
  type NetworkState,
  getNetworkState,
  isActiveLike,
  NetworkStateBadge,
  networkStateLabel,
} from '../../../LocationSwitcher';

const SiteSwitcherOptionLabel = styled('span', {
  flex: '1 1 auto',
});

const SiteSwitcherOptionHeading = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: '100%',
  gap: '$4',
});

const SiteSwitcherOptionMetadata = styled(Text, {
  ...SmallSansSizes,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: '100%',
});

const SiteSwitcherOptionContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
});

type SiteSwitcherNetwork = Pick<
  Network,
  'label' | 'isActive' | 'isCustomer' | 'isTrial' | 'isTemplate' | 'deactivatedAt' | 'goLiveAt'
>;

export function SiteSwitcherOption({
  network,
  address,
}: {
  network: SiteSwitcherNetwork;
  address?: string;
}) {
  return (
    <SiteSwitcherOptionContainer>
      <SiteSwitcherOptionHeading>
        <SiteSwitcherOptionLabel>{network.label}</SiteSwitcherOptionLabel>
        <NetworkStateBadge state={getNetworkState(network)} size="x-small" />
      </SiteSwitcherOptionHeading>
      {address && <SiteSwitcherOptionMetadata>{address}</SiteSwitcherOptionMetadata>}
    </SiteSwitcherOptionContainer>
  );
}

const SiteSwitcherInactiveToggle = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '$6',
});

const SiteSwitcherActions = styled('div', {
  display: 'flex',
  justifyContent: 'flex-end',
  width: '100%',
});

const SitesContainerSingle = styled(AlignStack, {
  '@notDesktop': {
    maxWidth: '100%',
  },

  '@desktop': {
    maxWidth: '$320',
  },
});

const SitesContainer = styled('div', {
  display: 'flex',
  width: '100%',

  '@desktop': {
    maxWidth: '$420',
  },

  [`& ${SiteSwitcherOptionMetadata}`]: {
    display: 'none',
  },
});

export function useIsMultisitePage() {
  const isActiveTest = useIsActiveMatcher();
  return useMemo(() => MULTISITE_PAGES.some((path) => isActiveTest({ path })), [isActiveTest]);
}

export function Sites({
  networkSlug: propNetworkSlug,
  shouldNotChangePages,
  routePathsNotToChange,
  fallback,
}: {
  routePathsNotToChange?: string[];
  networkSlug?: string;
  shouldNotChangePages?: boolean;
  fallback?: boolean;
}) {
  const { breakpoint } = useViewport();
  const companyName = useCurrentCompany();
  const navigate = useNavigate();

  const [showNonLiveNetworks, setShowNonLiveNetworks] = useState(false);

  const networkSlugFromPath = useNetworkSlugFromPathOrNull();
  const networks = useNetworksForCompany(companyName);
  const hasMultipleNetworks = networks.length > 1;
  const location = useLocation();
  const multisiteAccess = useAccessLevelForUser({ featureFlags: 'multisite-dashboard' });
  const isMultisitePage = useIsMultisitePage();

  const networkSlug = useMemo<string | undefined>(() => {
    if (propNetworkSlug) return propNetworkSlug;

    if (networkSlugFromPath) return networkSlugFromPath;

    return undefined;
  }, [propNetworkSlug, networkSlugFromPath]);

  const ref = useRef(null);

  const selectedNetwork = useMemo(
    () => networks.find((network) => network.slug === networkSlug),
    [networks, networkSlug],
  );

  useEffect(() => {
    if (!selectedNetwork) return;

    if (!isActiveLike(getNetworkState(selectedNetwork))) {
      setShowNonLiveNetworks(true);
    }
  }, [selectedNetwork]);

  const hasNonLiveNetworks = useMemo(
    () => networks.some((n) => !isActiveLike(getNetworkState(n))),
    [networks],
  );

  const networksToShow = useMemo(() => {
    let filteredNetworks = networks;
    if (!showNonLiveNetworks) {
      filteredNetworks = filteredNetworks.filter((network) =>
        isActiveLike(getNetworkState(network)),
      );
    }

    return filteredNetworks;
  }, [networks, showNonLiveNetworks]);

  const { crumbs } = useCurrentCrumbs();

  const valueSelected = useCallback(
    (selectedNetworkSlug: Key) => {
      if (typeof selectedNetworkSlug !== 'string')
        throw new Error('Unexpected key for selectedNetworkSlug');

      const network = networks.find((n) => n.slug === selectedNetworkSlug);
      const controllerName = getActiveControllerForNetwork(network ?? null)?.hardwareDevice
        ?.serialNumber;

      if (shouldNotChangePages && routePathsNotToChange?.length) {
        const pageMatch = routePathsNotToChange
          ?.map((path) =>
            matchPath(
              {
                path,
                end: false,
              },
              location.pathname,
            ),
          )
          .filter((match) => match);
        const currentPage = pageMatch[0];
        if (currentPage && currentPage?.params) {
          navigate(
            makeLink(currentPage.pattern.path, {
              ...currentPage.params,
              networkSlug: selectedNetworkSlug,
              controllerName,
            }),
          );
        }
      } else {
        const link = getNextBestCurrentPathForNetwork({
          companyName,
          networkSlug: selectedNetworkSlug,
          crumbs,
          currentPathname: location.pathname,
        });

        navigate(link);
      }
    },
    [
      companyName,
      location.pathname,
      navigate,
      networks,
      shouldNotChangePages,
      routePathsNotToChange,
      crumbs,
    ],
  );

  const sortedNetworks = useMemo(
    () =>
      networksToShow
        .map((network) => ({
          address: getNetworkAddress(network),
          network,
        }))
        .sort((a, b) => a.network.label.localeCompare(b.network.label)),
    [networksToShow],
  );

  const groupedNetworks = useMemo(() => {
    const map = new Map<NetworkState, typeof sortedNetworks>();

    for (const obj of sortedNetworks) {
      const networkState = getNetworkState(obj.network);
      let arr = map.get(networkState);
      if (!arr) {
        arr = [];
        map.set(networkState, arr);
      }
      arr.push(obj);
    }

    return Array.from(map.entries()).sort(([a], [b]) => a - b);
  }, [sortedNetworks]);

  return fallback ? (
    <SkeletonGroup direction="row" skeletons={[{ width: 200, height: 16, radius: 6 }]} />
  ) : (
    <SitesContainer>
      {hasMultipleNetworks ? (
        <ComboBox
          ref={ref}
          icon="location"
          onValueChange={valueSelected}
          value={networkSlug}
          aria-label="Switch location"
          canClearValue={false}
          ghost={breakpoint === 'desktop'}
          placeholder="Select a location"
          size={breakpoint === 'desktop' ? 'medium' : 'large'}
          width="100%"
          maxWidth={breakpoint === 'desktop' ? 320 : '100%'}
          actions={
            hasNonLiveNetworks && (
              <SiteSwitcherActions>
                <SiteSwitcherInactiveToggle>
                  <Label htmlFor="LocationSwitch-toggleActive">Show inactive networks</Label>
                  <ToggleInput
                    id="LocationSwitch-toggleActive"
                    selected={showNonLiveNetworks}
                    onChange={setShowNonLiveNetworks}
                    controlSize="small"
                  />
                </SiteSwitcherInactiveToggle>
              </SiteSwitcherActions>
            )
          }
        >
          <>
            {!!multisiteAccess && isMultisitePage ? (
              <ComboBoxItem key={ALL_NETWORKS_SLUG} textValue="All networks">
                <AlignStack gap={4} end={<FeatureBadge type={multisiteAccess} size="x-small" />}>
                  (All networks)
                </AlignStack>
              </ComboBoxItem>
            ) : null}
            {groupedNetworks.length === 1
              ? groupedNetworks[0]?.[1]?.map(({ network, address }) => (
                  <ComboBoxItem
                    key={network.slug}
                    textValue={address ? `${network.label} ${address}` : network.label}
                  >
                    <SiteSwitcherOption network={network} address={address} />
                  </ComboBoxItem>
                ))
              : groupedNetworks.map(([state, groupNetworks]) => (
                  <ComboBoxSection key={state} title={networkStateLabel(state)}>
                    {groupNetworks.map(({ network, address }) => (
                      <ComboBoxItem
                        key={network.slug}
                        textValue={address ? `${network.label} ${address}` : network.label}
                      >
                        <SiteSwitcherOption network={network} address={address} />
                      </ComboBoxItem>
                    ))}
                  </ComboBoxSection>
                ))}
          </>
        </ComboBox>
      ) : (
        <SitesContainerSingle
          align="center"
          gap={6}
          width="min-content"
          preset="body"
          start={<Icon icon="location" size={14} variant="neutral" />}
          end={
            selectedNetwork?.isTrial && (
              <Badge ends="card" size="small" variant="brand">
                Trial
              </Badge>
            )
          }
        >
          <Text weight="bold" whitespace="no-wrap" truncate>
            {selectedNetwork?.label}
          </Text>
        </SitesContainerSingle>
      )}
    </SitesContainer>
  );
}
